<template>
  <figure class="min-w-0">
    <figcaption class="text-background-fg/70 mb-3 text-xs font-medium">
      {{ title }}
    </figcaption>
    <div class="relative">
      <div
        class="border-background-fg/20 flex h-32 items-end border-b"
        role="list"
        :aria-label="title"
      >
        <div
          v-for="row in rows"
          :key="row.bucket"
          role="listitem"
          tabindex="0"
          class="focus-visible:ring-primary relative flex h-full flex-1 flex-col items-center justify-end rounded-sm outline-none focus-visible:ring-2"
          style="padding: 0 1px"
          :aria-label="describe(row)"
          @mouseenter="active = row.bucket"
          @mouseleave="active = null"
          @focus="active = row.bucket"
          @blur="active = null"
        >
          <span
            v-if="row.bucket === peak?.bucket && row.total > 0"
            class="text-background-fg/70 mb-1 text-xs font-semibold tabular-nums"
          >
            {{ row.total }}
          </span>
          <div
            class="bg-primary w-full"
            :class="active === row.bucket ? 'opacity-100' : 'opacity-80'"
            :style="{
              maxWidth: '24px',
              height: barHeight(row),
              borderRadius: '4px 4px 0 0',
            }"
          ></div>
          <div
            v-if="active === row.bucket"
            class="pointer-events-none absolute bottom-full z-10 mb-1 whitespace-nowrap rounded-md bg-white px-2.5 py-1.5 text-xs shadow-lg ring-1 ring-black/5"
          >
            <p class="font-semibold text-black/90">{{ row.label }}</p>
            <p class="text-background-fg/70 tabular-nums">
              {{ row.total }} through the gates
            </p>
            <p class="text-background-fg/50 tabular-nums">
              {{ row.totalIn }} in, {{ row.totalOut }} out
            </p>
          </div>
        </div>
      </div>
      <div class="flex pt-1.5">
        <span
          v-for="(row, index) in rows"
          :key="row.bucket"
          class="text-background-fg/50 flex-1 text-center text-xs tabular-nums"
          aria-hidden="true"
        >
          {{ index % labelEvery === 0 ? shortLabel(row) : "" }}
        </span>
      </div>
    </div>
  </figure>
</template>

<script setup lang="ts">
import { type StorageAccessTime } from "@/services/reports/storageReports";

const props = withDefaults(
  defineProps<{
    title: string;
    rows: StorageAccessTime[];
    /** Label every nth column on the axis; the tooltip names them all. */
    labelEvery?: number;
  }>(),
  { labelEvery: 1 },
);

const active = ref<number | null>(null);

const peak = computed(() =>
  props.rows.reduce<StorageAccessTime | null>(
    (best, row) => (best == null || row.total > best.total ? row : best),
    null,
  ),
);

const barHeight = (row: StorageAccessTime) => {
  const max = peak.value?.total ?? 0;
  if (!max || !row.total) return "0px";
  // Leave room above the tallest column for its value label.
  return `max(2px, ${(row.total / max) * 80}%)`;
};

// "07:00" reads as "07" on a crowded hour axis; months keep their year only
// when the range spans more than one.
const shortLabel = (row: StorageAccessTime) => {
  if (row.dimension === "hour") return row.label.slice(0, 2);
  if (row.dimension === "month") {
    const years = new Set(props.rows.map((r) => r.label.slice(-4)));
    return years.size > 1 ? row.label : row.label.slice(0, 3);
  }
  return row.label;
};

const describe = (row: StorageAccessTime) =>
  `${row.label}: ${row.total} (${row.totalIn} in, ${row.totalOut} out)`;
</script>
